/** Doplňkové generické widgety pro layout engine: gauge (ukazatel), wind-gauge, automation-status.
 *  boolean-timeline se renderuje přímo přes SubscribedChart (boolean) v layoutWidgets.tsx. */

import React, { useEffect, useState } from "react";
import { getAutomations, type AutomationSummary } from "@client/api/automations";
import WindGauge from "@client/components/valueDisplays/elementary/WindGauge";
import { colors } from "@client/constants";
import { useDeviceTopicLive } from "@client/hooks/useDeviceTopicLive";
import { useTopic } from "@client/hooks/useTopic";
import { cn } from "@client/lib/cn";
import { nickname } from "@client/utils/nickname";
import { fix, number, unit, unundefined } from "@client/utils/values";

/** Ukazatel: hodnota v rozsahu [min,max] jako vyplněný pruh + popisek. `variant` škáluje (sm = buňka tabulky). */
export const GaugeWidget: React.FC<{ topicId: string; min?: number; max?: number; variant?: "lg" | "md" | "sm" }> = ({ topicId, min, max, variant = "lg" }) => {
  const { value: slowValue, topic } = useTopic(topicId);
  const live = useDeviceTopicLive(topicId); // device stránky: rychlá ~1 Hz hodnota; jinak undefined → MQTT
  const value = live && !live.stale ? live.value : slowValue;
  const lo = Number.isFinite(min) ? (min as number) : 0;
  const hiRaw = Number.isFinite(max) ? (max as number) : 100;
  const hi = hiRaw === lo ? lo + 100 : hiRaw;
  const v = typeof value === "number" ? value : Number(value);
  const pct = Number.isFinite(v) ? Math.max(0, Math.min(100, ((v - lo) / (hi - lo)) * 100)) : 0;
  const display = unit(unundefined(fix(number(String(value)), topic?.decimals ?? 1), "---"), topic?.unit ?? "");
  const sm = variant === "sm";
  return (
    <div className={sm ? "flex flex-col gap-1 px-1.5 py-1" : "flex flex-col gap-1.5 p-3"}>
      <div className="flex items-center justify-between gap-2">
        <span className={sm ? "truncate text-[10px] text-muted-foreground" : "truncate text-sm font-medium text-muted-foreground"}>{nickname(topic)}</span>
        <span className={sm ? "text-xs font-semibold tabular-nums" : "text-sm font-bold tabular-nums"} style={{ color: colors[0] }}>{display}</span>
      </div>
      <div className={sm ? "h-1.5 w-full overflow-hidden rounded-full bg-muted" : "h-2 w-full overflow-hidden rounded-full bg-muted"}>
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: colors[0] }} />
      </div>
      {!sm && (
        <div className="flex justify-between text-[10px] text-muted-foreground">
          <span>{lo}</span>
          <span>{hi}</span>
        </div>
      )}
    </div>
  );
};

/** Větrná růžice — směr ze živé hodnoty topicu (stupně). */
export const WindGaugeWidget: React.FC<{ topicId: string }> = ({ topicId }) => {
  const { value } = useTopic(topicId);
  const dir = typeof value === "number" ? value : Number(value);
  return (
    <div className="p-2">
      <WindGauge direction={Number.isFinite(dir) ? dir : 0} valueF={(d) => `${Math.round(d)}°`} />
    </div>
  );
};

/** Stav automatizací (z /api/automations). Prázdné automationId = všechny. */
export const AutomationStatusWidget: React.FC<{ automationId?: string }> = ({ automationId }) => {
  const [rows, setRows] = useState<AutomationSummary[]>([]);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let alive = true;
    getAutomations()
      .then((r) => alive && r?.success && setRows(r.responseObject ?? []))
      .catch(() => alive && setErr(true));
    return () => {
      alive = false;
    };
  }, []);
  if (err) return <div className="p-2 text-xs text-muted-foreground">Automatizace nedostupné</div>;
  const list = automationId ? rows.filter((a) => a.id === automationId) : rows;
  return (
    <div className="flex flex-col gap-1 p-2 text-sm">
      {list.length === 0 && <span className="text-xs text-muted-foreground">Žádné automatizace</span>}
      {list.map((a) => (
        <div key={a.id} className="flex items-center justify-between gap-2">
          <span className="truncate">{a.name}</span>
          <span className={cn("shrink-0 rounded px-1.5 text-xs", a.enabled ? "bg-status-ok text-white" : "bg-muted text-muted-foreground")}>
            {a.enabled ? "zap" : "vyp"}
          </span>
        </div>
      ))}
    </div>
  );
};
